/**
 * EmailDraftOutput — structured three-block output for the Email Drafter
 * (Phase X.3.4 → X.3.4.5 → X.3.4.6 → A2a).
 *
 * X.3.4.7: the redundant inner body heading was dropped; the per-tool
 * "Your draft" output-section header (ToolPageShell outputTitle) carries
 * the labeling now. The body+annotations+billing rendering here is
 * reusable; the Save row lives in the EmailDrafter page.
 *
 * A2a: the email body is now an EDITABLE textarea (controlled via the
 * `body` + `onBodyChange` props the EmailDrafter page owns), so a CM can
 * tweak the wording in the workspace BEFORE saving — removing the old
 * "save before you can edit" friction. Copy + Save both act on the edited
 * `body`, not the original `response.email_body`. Annotations + billing
 * still ground in the ORIGINAL generation (the structured response), so
 * they remain read-only.
 *
 * X.3.4.6: the email body INCLUDES the model-generated salutation +
 * "Warmly, <name>" valediction (per the X.3.4.6 prompt §2.1 rewrite). The
 * textarea preserves the whitespace, so those lines edit naturally.
 *
 * Mobile-first stacked layout; gap-4 between blocks.
 */

import { type ReactNode, useRef } from 'react';
import type { EmailDraftResponse } from '@/lib/api';
import { EmailAnnotationsBlock } from './EmailAnnotationsBlock';
import { EmailBillingBlock } from './EmailBillingBlock';
import { LogAsCaseNoteAction } from './LogAsCaseNoteAction';
import { EmailActionRows } from './EmailActionRows';
import { SpicePhotoReminder } from './SpicePhotoReminder';
import { SpiceMedicalFlag } from './SpiceMedicalFlag';
import type { EmailMode } from './EmailModeToggle';

export interface EmailDraftOutputProps {
  response: EmailDraftResponse;
  // X.3.4.10: 'spice' hides billing (the response carries none) and shows
  // the photo/video attach reminder. Defaults to Care Management.
  mode?: EmailMode;
  // A2a: the editable body is lifted to the EmailDrafter page (controlled),
  // so Save + Copy act on the edited text and the page can persist it.
  body: string;
  onBodyChange: (next: string) => void;
  /** Quiet actions supplied by the page, after Copy draft email. */
  actions?: ReactNode;
  /** The client the page's "For" line names, on either face: the filing
   *  door goes straight to that record instead of asking. */
  clientId?: string | null;
}

const BODY_TEXTAREA_CLS =
  'w-full min-h-64 whitespace-pre-wrap font-sans text-[0.90625rem] leading-relaxed text-ink bg-paper border border-sage-800/[8%] rounded-[0.75rem] p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';

export function EmailDraftOutput({
  response,
  mode = 'care_management',
  body,
  onBodyChange,
  actions,
  clientId,
}: EmailDraftOutputProps): ReactNode {
  // The body's textarea, where a note pinned to its words selects them.
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const isSpice = mode === 'spice';

  return (
    <div className="space-y-4" data-testid="email-draft-output">
      {/* The medical or care content in this email, named above everything
          else on the SPICE face; the email itself is untouched. */}
      {isSpice && response.medical_flag && <SpiceMedicalFlag text={response.medical_flag} />}
      {/* X.3.4.10: SPICE reminder to attach photos/videos before sending. */}
      {isSpice && <SpicePhotoReminder />}

      {/* Block 1: editable email body (A2a). The outer "Your draft"
          output-section header (ToolPageShell outputTitle) carries the
          labeling. */}
      <section data-testid="email-body-block" aria-label="Email body">
        <textarea
          ref={bodyRef}
          className={BODY_TEXTAREA_CLS}
          value={body}
          onChange={(e) => onBodyChange(e.target.value)}
          aria-label="Email draft body"
          data-testid="email-body-input"
        />
        <EmailActionRows
          body={body}
          logAction={
            <LogAsCaseNoteAction
              body={body}
              suggestedHours={isSpice ? undefined : response.billing_suggestion?.hours}
              spice={isSpice}
              clientId={clientId ?? null}
            />
          }
          actions={actions}
        />
      </section>

      {/* Block 2: annotations (renders null when no note shows: a note
          pinned to its words shows while they are in the body) */}
      <EmailAnnotationsBlock annotations={response.annotations} body={body} bodyRef={bodyRef} />

      {/* Block 3: billing suggestion. X.3.4.10: present only for Care
          Management; SPICE responses carry no billing_suggestion, so the
          block is suppressed (guard on presence). */}
      {response.billing_suggestion && (
        <EmailBillingBlock suggestion={response.billing_suggestion} />
      )}
    </div>
  );
}
